import React, { Suspense, lazy } from 'react';

import { useLocation } from 'react-router-dom';

import { Button, CircularProgress, Container, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { lensIdFromPath } from '~/pages/AskRichardUnified/lensPath';
import { decodeShellView } from '~/pages/AskRichardUnified/shellUrlState';
import { SurfacePlate } from '~/pages/LeLens/surface';

import { useLensAccessDecision } from '~/hooks/useCanAccessLeLens';

import LensAccessCheckError from './LensAccessCheckError';
import SharedPersonalLens from './personalViews/SharedPersonalLens';

const SharedLensPage = lazy(() => import('./LeLensPage'));
const LENS_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type LensAccessGateProps = { children: React.ReactNode };

/** The Lens a shared link points at, or null when the URL is not a valid Lens link. */
export function sharedLensIdFor(pathname: string, search: string): string | null {
  const legacyId = pathname.match(/^\/le-lens\/([^/]+)\/?$/)?.[1];
  const state = decodeShellView(new URLSearchParams(search));
  const lensId =
    lensIdFromPath(pathname) ??
    legacyId ??
    (/^\/ask-richard-unified\/?$/.test(pathname) && state.view === 'lens' ? state.lensId : null);
  return lensId && LENS_ID.test(lensId) ? lensId : null;
}

/**
 * True when the gate renders a shared Lens without the askRichard shell. That page has no scroll
 * pane of its own, so MasterPage must not apply the fixed workspace layout to it. While the access
 * check is loading this stays false, so pilot users never see the layout jump.
 */
export function showsSharedLensFallback(
  pathname: string,
  search: string,
  access: { canAccess: boolean; isLoading: boolean },
): boolean {
  return !access.isLoading && !access.canAccess && sharedLensIdFor(pathname, search) !== null;
}

export default function LensAccessGate({ children }: LensAccessGateProps) {
  const { canAccess, isLoading, isError, retryAccess } = useLensAccessDecision();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lensId = sharedLensIdFor(location.pathname, location.search);

  if (isLoading) {
    return (
      <Container>
        <CircularProgress aria-label="Checking askRichard access" />
      </Container>
    );
  }

  if (isError) {
    return <LensAccessCheckError onRetry={retryAccess} />;
  }

  if (!canAccess && lensId) {
    return (
      <Suspense fallback={null}>
        <main>
          <SharedPersonalLens key={lensId} lensId={lensId}>
            <SharedLensPage lensId={lensId} selectedCardId={params.get('card')?.trim() || null} />
          </SharedPersonalLens>
        </main>
      </Suspense>
    );
  }

  if (!canAccess) {
    return (
      <Container>
        <PageHeader title="askRichard" />
        <SurfacePlate padding="plate">
          <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
            askRichard is in a closed pilot.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
            Today it&apos;s open to reporting admins and a small pilot list. To be added, email{' '}
            <a href="mailto:data@luxuryescapes.com">data@luxuryescapes.com</a> and say what you&apos;d use it for.
          </Typography>
          <Button onClick={() => retryAccess()} sx={{ mt: 2 }}>
            Check access again
          </Button>
        </SurfacePlate>
      </Container>
    );
  }

  return <>{children}</>;
}
